import {
  AppPageHeader,
  DeleteIcon,
  EditIcon,
  InfiniteTable,
} from '@/components/_common';
import {
  useCustomerCategoryList,
  useCustomerCategoryRemove,
} from '@/core/domain/people/CustomerCategory/CustomerCategory.hooks';
import {
  CustomerCategory,
  ListCustomersCategoryQuery,
} from '@/core/domain/people/CustomerCategory/CustomerCategory.types';
import { useDebounce, useDisclosure } from '@/hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Divider, Modal, Space, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import CategoryModal from '../CategoryModal';

interface Props {
  enabled: boolean;
}

export default function CategoryList({ enabled = true }: Props) {
  const [queryParams, setQueryParams] = useState<ListCustomersCategoryQuery>({
    order: 'ASC',
    ...convertParams<ListCustomersCategoryQuery>({
      name: 'string',
    }),
  });

  const [categoryId, setCategoryId] = useState<number>();
  const [modalEditOpened, modalEditActions] = useDisclosure();

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const list = useCustomerCategoryList(queryDebounce, enabled);

  const categories = flatInfiniteData(list.data);

  const remove = useCustomerCategoryRemove();

  const handleRemoveClick = (id: number) => {
    Modal.confirm({
      title: 'Atenção!',
      content: 'Deseja realmente excluir essa categoria?',
      onOk: async () => {
        await remove.mutateAsync(id);
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<CustomerCategory> = [
    {
      dataIndex: 'name',
      title: 'Descrição',
      render: (_, record) => (
        <Tag color={record.color}>{record.name.toUpperCase()}</Tag>
      ),
    },
    {
      dataIndex: 'actions',
      title: 'Ações',
      render: (_, row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                setCategoryId(row.id);
                modalEditActions.open();
              },
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon onClick={() => handleRemoveClick(row.id)} />
        </Space>
      ),
      width: 100,
      align: 'right',
      fixed: 'right',
    },
  ];

  return (
    <>
      <AppPageHeader
        searchValue={queryParams.name}
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, name: value }))
        }
      />

      <InfiniteTable<CustomerCategory>
        columns={columns}
        dataSource={categories}
        loading={list.isFetching}
        rowKey={(row) => row.id}
        hasNextPage={list.hasNextPage}
        fetchNextPage={list.fetchNextPage}
      />

      <CategoryModal
        categoryId={categoryId}
        open={modalEditOpened}
        onCancel={() => {
          modalEditActions.close();
          setCategoryId(undefined);
        }}
      />
    </>
  );
}
